import { CdkTrapFocus } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map } from 'rxjs';
import { SidenavService } from '../sidenav.service';
import { CompanyDropdownComponent } from '../company-dropdown/company-dropdown.component';
import { AuthService } from '@core/auth/auth.service';
import { Role } from '@core/auth/roles';
import { CompanyContextService } from '@core/tenancy/company-context.service';

interface NavLinkItem {
  kind: 'link';
  label: string;
  path: string;
  icon: string;
  /** When set, the item is only shown to users holding this role. */
  requiredRole?: Role;
  /** When true, the item is only shown once a company is selected in the header dropdown. */
  requiresCompanySelected?: boolean;
}

interface NavChildLink {
  label: string;
  path: string;
}

interface NavGroupItem {
  kind: 'group';
  id: string;
  label: string;
  icon: string;
  basePath: string;
  children: NavChildLink[];
  /** When true, the item is only shown once a company is selected in the header dropdown. */
  requiresCompanySelected?: boolean;
}

type NavItem = NavLinkItem | NavGroupItem;

@Component({
  selector: 'app-sidenav',
  imports: [RouterLink, RouterLinkActive, CdkTrapFocus, CompanyDropdownComponent],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent {
  protected readonly sidenavService = inject(SidenavService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly companyContext = inject(CompanyContextService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  protected readonly mode = this.sidenavService.mode;
  private readonly closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');

  /** Rail only (F17): the group whose sub-menu is open beside the rail. */
  protected readonly flyoutGroupId = signal<string | null>(null);

  protected readonly navItems: NavItem[] = [
    {
      kind: 'link',
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    },
    {
      kind: 'link',
      label: 'Company',
      path: '/company',
      icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    },
    {
      kind: 'link',
      label: 'Customers',
      path: '/customers',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
      requiresCompanySelected: true,
    },
    {
      kind: 'link',
      label: 'Waterbars',
      path: '/waterbars',
      icon: 'M12 2.69l5.66 5.66a8 8 0 11-11.31 0z',
      requiredRole: Role.SystemAdmin,
      requiresCompanySelected: true,
    },
    {
      kind: 'group',
      id: 'handyman',
      label: 'Handyman',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      basePath: '/handyman',
      children: [
        { label: 'JobCard Board', path: '/handyman/board' },
        { label: 'Price Calculator', path: '/handyman/price-calculator' },
        { label: 'Cost Categories', path: '/handyman/cost-categories' },
        { label: 'Pricing Settings', path: '/handyman/pricing-settings' },
      ],
      requiresCompanySelected: true,
    },
    {
      kind: 'link',
      label: 'Users',
      path: '/users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
      requiredRole: Role.SystemAdmin,
    },
  ];

  protected readonly visibleNavItems = computed(() => {
    const companySelected = this.companyContext.selected() !== null;
    return this.navItems.filter((item) => {
      if (item.kind === 'link' && item.requiredRole && !this.authService.hasRole(item.requiredRole)) {
        return false;
      }
      if (item.requiresCompanySelected && !companySelected) {
        return false;
      }
      return true;
    });
  });

  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly expandedGroups = signal<Set<string>>(new Set());

  protected readonly sidebarClasses = computed(() => {
    // Static from `lg` up: a 72px rail to `xl`, then the full 248px sidebar. Below `lg` it's an overlay drawer that
    // `isOpen()` toggles; the `lg:` utilities override the drawer positioning.
    const base =
      'flex-col bg-surface border-r border-border shrink-0 lg:flex lg:static lg:z-auto lg:inset-auto lg:w-18 lg:max-w-none lg:overflow-visible xl:w-62';
    return this.sidenavService.isOpen()
      ? `${base} flex fixed inset-y-0 left-0 z-30 w-62 max-w-[85vw] overflow-y-auto`
      : `${base} hidden`;
  });

  constructor() {
    // The drawer moves focus to its close button when it opens (and traps it until closed).
    effect(() => {
      if (!this.sidenavService.isOpen()) return;
      afterNextRender(() => this.closeButton()?.nativeElement.focus(), { injector: this.injector });
    });

    // Changing page or leaving the rail closes the flyout.
    effect(() => {
      this.currentUrl();
      this.mode();
      this.flyoutGroupId.set(null);
    });

    // The inline sub-menu (drawer and full sidebar) opens on its own pages; the rail flyout never opens by itself.
    effect(() => {
      const url = this.currentUrl();
      for (const item of this.navItems) {
        if (item.kind === 'group' && url.startsWith(item.basePath)) {
          this.expandedGroups.update((set) => (set.has(item.id) ? set : new Set(set).add(item.id)));
        }
      }
    });
  }

  protected trackItem(item: NavItem): string {
    return item.kind === 'link' ? item.path : item.id;
  }

  protected isGroupActive(group: NavGroupItem): boolean {
    return this.currentUrl().startsWith(group.basePath);
  }

  protected isChildActive(child: NavChildLink): boolean {
    const url = this.currentUrl();
    return url === child.path || url.startsWith(child.path + '/');
  }

  protected isExpanded(group: NavGroupItem): boolean {
    return this.expandedGroups().has(group.id);
  }

  protected toggleGroup(group: NavGroupItem): void {
    if (this.mode() === 'rail') {
      this.flyoutGroupId.update((id) => (id === group.id ? null : group.id));
      return;
    }
    this.expandedGroups.update((set) => {
      const next = new Set(set);
      if (next.has(group.id)) {
        next.delete(group.id);
      } else {
        next.add(group.id);
      }
      return next;
    });
  }
  /** Following a link closes the drawer (focus stays on the new page) and the rail flyout. */
  protected onNavigate(): void {
    this.sidenavService.close(false);
    this.flyoutGroupId.set(null);
  }

  /** Escape closes the rail flyout (focus back to its button), else the drawer (focus back to the menu button). */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    const flyout = this.flyoutGroupId();
    if (flyout) {
      this.flyoutGroupId.set(null);
      this.host.nativeElement.querySelector<HTMLButtonElement>(`#nav-group-${flyout}`)?.focus();
      return;
    }
    this.sidenavService.close();
  }

  /** A click outside the open flyout's group closes it. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const flyout = this.flyoutGroupId();
    if (!flyout) return;
    const group = this.host.nativeElement.querySelector(`[data-group="${flyout}"]`);
    if (!group?.contains(event.target as Node)) this.flyoutGroupId.set(null);
  }
}
